/** Draft string state for Audio Editor time fields (TASK-00168). */

import { useCallback, useEffect, useState } from 'react'
import {
  adjustDurationKeepingStart,
  adjustEndKeepingStart,
  adjustStartKeepingEnd,
  clampSelectionToSource,
  formatAudioTimeMs,
  formatDurationSecondsMs,
  parseAudioTimeInput,
  parseDurationSecondsInput,
  selectionDurationMs,
  type TrimSelectionMs,
} from './audioTrimTime'

export function useAudioEditorDraftInputs(
  selection: TrimSelectionMs,
  durationMs: number,
  onSelectionChange: (next: TrimSelectionMs) => void,
) {
  const [startDraft, setStartDraft] = useState('')
  const [durationDraft, setDurationDraft] = useState('')
  const [endDraft, setEndDraft] = useState('')

  useEffect(() => {
    setStartDraft(formatAudioTimeMs(selection.startMs))
    setDurationDraft(formatDurationSecondsMs(selectionDurationMs(selection)))
    setEndDraft(formatAudioTimeMs(selection.endMs))
  }, [selection.startMs, selection.endMs])

  const commitStart = useCallback(() => {
    const parsed = parseAudioTimeInput(startDraft)
    if (parsed == null || !durationMs) return
    onSelectionChange(
      clampSelectionToSource(adjustStartKeepingEnd(parsed, selection.endMs, durationMs), durationMs),
    )
  }, [durationMs, onSelectionChange, selection.endMs, startDraft])

  const commitDuration = useCallback(() => {
    const parsed = parseDurationSecondsInput(durationDraft)
    if (parsed == null || !durationMs) return
    onSelectionChange(
      clampSelectionToSource(adjustDurationKeepingStart(selection.startMs, parsed, durationMs), durationMs),
    )
  }, [durationDraft, durationMs, onSelectionChange, selection.startMs])

  const commitEnd = useCallback(() => {
    const parsed = parseAudioTimeInput(endDraft)
    if (parsed == null || !durationMs) return
    onSelectionChange(
      clampSelectionToSource(adjustEndKeepingStart(selection.startMs, parsed, durationMs), durationMs),
    )
  }, [durationMs, endDraft, onSelectionChange, selection.startMs])

  return {
    startDraft,
    setStartDraft,
    commitStart,
    durationDraft,
    setDurationDraft,
    commitDuration,
    endDraft,
    setEndDraft,
    commitEnd,
  }
}
